'use client';

import type { BrandConfig } from '@lib/config/brand';
import { cn } from '@lib/util/cn';
import LocalizedClientLink from '@modules/common/components/localized-client-link';
import Image from 'next/image';
import { useEffect, useState } from 'react';

// Returns the compact/scrolled logo src when a distinct one exists, else null.
// Extracted to keep NavLogoClient's cyclomatic complexity within threshold.
function getScrolledLogo(
  brand: BrandConfig,
  disableScrollSwap: boolean,
): string | null {
  if (disableScrollSwap) {
    return null;
  }
  const candidate = brand.scrolledLogo ?? brand.logo;
  if (candidate === brand.logo) {
    return null;
  }
  return candidate;
}

export default function NavLogoClient({
  brand,
  disableScrollSwap = false,
}: {
  brand: BrandConfig;
  disableScrollSwap?: boolean;
}) {
  const [scrolled, setScrolled] = useState(false);
  const scrolledLogo = getScrolledLogo(brand, disableScrollSwap);

  useEffect(() => {
    if (!scrolledLogo) {
      return;
    }
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [scrolledLogo]);

  return (
    <LocalizedClientLink
      href="/"
      prefetch={false}
      data-testid="nav-store-link"
      aria-label={`${brand.name} home`}
      className="rounded focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-white"
    >
      {/* Full logo — always on desktop, on mobile only when not scrolled */}
      <Image
        src={brand.logo}
        alt={brand.name}
        width={188}
        height={44}
        className={cn(
          'max-[375px]:h-7 h-9 desktop:h-[44px] w-auto desktop:block',
          {
            hidden: !!scrolledLogo && scrolled,
          },
        )}
        fetchPriority="high"
        loading="eager"
      />
      {/* Compact icon — mobile only, shown when scrolled */}
      {scrolledLogo && (
        <Image
          src={scrolledLogo}
          alt={brand.name}
          width={44}
          height={44}
          className={cn('h-[44px] w-auto desktop:hidden', {
            hidden: !scrolled,
          })}
        />
      )}
    </LocalizedClientLink>
  );
}
